import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Copy, Briefcase, Star, ExternalLink } from "lucide-react";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const ROLE_COLORS = ["#005BBB", "#E3000F", "#8B5CF6", "#0EA5E9", "#F97316", "#4CAF50", "#FFB800", "#0D1A3F"];

const emptyForm = {
  name_es: "", name_en: "", description_es: "", description_en: "",
  required_exam_ids: [], bonus_exam_ids: [], color: "#005BBB", icon: "briefcase",
};

export default function AdminRoles() {
  const { lang } = useLang();
  const [roles, setRoles] = useState([]);
  const [exams, setExams] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // role_id or null
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const [r, e] = await Promise.all([api.get("/admin/roles"), api.get("/admin/exams")]);
    setRoles(r.data);
    setExams(e.data);
  };
  useEffect(() => { load(); }, []);

  const examById = useMemo(() => Object.fromEntries(exams.map((e) => [e.exam_id, e])), [exams]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (role) => {
    setEditing(role.role_id);
    setForm({
      name_es: role.name_es || "",
      name_en: role.name_en || "",
      description_es: role.description_es || "",
      description_en: role.description_en || "",
      required_exam_ids: role.required_exam_ids || [],
      bonus_exam_ids: role.bonus_exam_ids || [],
      color: role.color || "#005BBB",
      icon: role.icon || "briefcase",
    });
    setOpen(true);
  };

  const toggleReq = (eid) => {
    setForm((f) => ({
      ...f,
      required_exam_ids: f.required_exam_ids.includes(eid)
        ? f.required_exam_ids.filter((x) => x !== eid)
        : [...f.required_exam_ids, eid],
      // remove from bonus if it's now required
      bonus_exam_ids: f.bonus_exam_ids.filter((x) => x !== eid),
    }));
  };

  const toggleBonus = (eid) => {
    setForm((f) => ({
      ...f,
      bonus_exam_ids: f.bonus_exam_ids.includes(eid)
        ? f.bonus_exam_ids.filter((x) => x !== eid)
        : [...f.bonus_exam_ids, eid],
      required_exam_ids: f.required_exam_ids.filter((x) => x !== eid),
    }));
  };

  const submit = async () => {
    if (!form.name_es.trim()) {
      toast.error(lang === "es" ? "Escribe el nombre del rol" : "Enter role name");
      return;
    }
    if (form.required_exam_ids.length === 0) {
      toast.error(lang === "es" ? "Elige al menos 1 examen obligatorio" : "Pick at least 1 required exam");
      return;
    }
    try {
      if (editing) {
        await api.put(`/admin/roles/${editing}`, form);
        toast.success(lang === "es" ? "Rol actualizado" : "Role updated");
      } else {
        await api.post("/admin/roles", form);
        toast.success(lang === "es" ? "Rol creado" : "Role created");
      }
      setOpen(false);
      setForm(emptyForm);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const remove = async (role) => {
    if (!window.confirm(lang === "es" ? `¿Eliminar rol "${role.name_es}"?` : `Delete role "${role.name_en}"?`)) return;
    await api.delete(`/admin/roles/${role.role_id}`);
    load();
  };

  const publicUrl = (slug) => `${window.location.origin}/onboarding/${slug}`;
  const copyUrl = async (slug) => {
    await navigator.clipboard.writeText(publicUrl(slug));
    toast.success(lang === "es" ? "¡Link copiado!" : "Link copied!");
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-4xl flex items-center gap-2">
            <Briefcase className="w-8 h-8 text-canales-red" />
            {lang === "es" ? "Roles / Rutas" : "Roles / Paths"}
          </h1>
          <p className="font-body text-neutral-600 mt-1">
            {lang === "es"
              ? "Crea rutas de onboarding por rol (Ventas Junior, Ventas Sénior, etc.) con exámenes obligatorios + bonus."
              : "Create per-role onboarding paths (Junior Sales, Senior Sales, etc.) with required + bonus exams."}
          </p>
        </div>
        <Button
          data-testid="admin-role-create-btn"
          onClick={openCreate}
          className="brutal-btn bg-canales-red text-white px-4 py-2 flex items-center gap-1"
        >
          <Plus className="w-4 h-4" /> {lang === "es" ? "Nuevo rol" : "New role"}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {roles.map((r) => (
          <div
            key={r.role_id}
            className="brutal p-5 bg-white"
            data-testid={`admin-role-row-${r.slug}`}
            style={{ borderColor: r.color }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border-2 border-black text-xs font-display font-bold text-white uppercase tracking-widest mb-2"
                  style={{ background: r.color }}
                >
                  {lang === "es" ? "Rol" : "Role"}
                </div>
                <h3 className="font-display font-black text-2xl truncate">
                  {lang === "es" ? r.name_es : r.name_en}
                </h3>
                <p className="font-body text-sm text-neutral-600 mt-1 line-clamp-2">
                  {lang === "es" ? r.description_es : r.description_en}
                </p>
                <div className="mt-2 text-sm font-body flex flex-wrap gap-3">
                  <span className="font-display font-bold">
                    {r.required_exam_ids.length} {lang === "es" ? "obligatorios" : "required"}
                  </span>
                  <span className="text-neutral-500">·</span>
                  <span className="text-neutral-700 flex items-center gap-1">
                    <Star className="w-3 h-3 fill-canales-yellow text-black" />
                    {r.bonus_exam_ids.length} bonus
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-3 text-xs font-body break-all bg-canales-cream brutal-sm p-2">
              {publicUrl(r.slug)}
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={() => copyUrl(r.slug)}
                data-testid={`admin-role-copy-${r.slug}`}
                className="brutal-btn bg-canales-yellow px-3 py-1 text-sm flex items-center gap-1"
              >
                <Copy className="w-4 h-4" /> {lang === "es" ? "Copiar link" : "Copy link"}
              </button>
              <a
                href={publicUrl(r.slug)}
                target="_blank"
                rel="noreferrer"
                className="brutal-btn bg-white px-3 py-1 text-sm flex items-center gap-1"
              >
                <ExternalLink className="w-4 h-4" /> {lang === "es" ? "Abrir" : "Open"}
              </a>
              <button
                onClick={() => openEdit(r)}
                data-testid={`admin-role-edit-${r.slug}`}
                className="brutal-btn bg-white px-3 py-1 text-sm flex items-center gap-1"
              >
                <Edit3 className="w-4 h-4" /> {lang === "es" ? "Editar" : "Edit"}
              </button>
              <button
                onClick={() => remove(r)}
                className="brutal-btn bg-red-500 text-white px-3 py-1 text-sm flex items-center gap-1"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {roles.length === 0 && (
          <div className="brutal p-8 bg-white text-center col-span-full font-body text-neutral-600">
            {lang === "es"
              ? "Aún no hay roles. Crea tu primer rol para armar rutas por Vendedor Junior, Sénior, etc."
              : "No roles yet. Create your first role to build paths per Junior, Senior Seller, etc."}
          </div>
        )}
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-3xl brutal border-black bg-white max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl">
              {editing
                ? (lang === "es" ? "Editar rol" : "Edit role")
                : (lang === "es" ? "Nuevo rol" : "New role")}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nombre ES</Label>
                <Input
                  data-testid="admin-role-name-es"
                  value={form.name_es}
                  onChange={(e) => setForm({ ...form, name_es: e.target.value })}
                  placeholder="Ventas Junior"
                />
              </div>
              <div>
                <Label>Name EN</Label>
                <Input
                  data-testid="admin-role-name-en"
                  value={form.name_en}
                  onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                  placeholder="Junior Sales"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{lang === "es" ? "Descripción ES" : "Description ES"}</Label>
                <Textarea
                  rows={2}
                  value={form.description_es}
                  onChange={(e) => setForm({ ...form, description_es: e.target.value })}
                  placeholder={lang === "es" ? "Para nuevos vendedores en su primer mes." : ""}
                />
              </div>
              <div>
                <Label>Description EN</Label>
                <Textarea
                  rows={2}
                  value={form.description_en}
                  onChange={(e) => setForm({ ...form, description_en: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>{lang === "es" ? "Color del rol" : "Role color"}</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {ROLE_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    className={`w-10 h-10 rounded-full border-2 border-black ${form.color === c ? "ring-4 ring-canales-yellow" : ""}`}
                    style={{ background: c }}
                    aria-label={c}
                  />
                ))}
              </div>
            </div>

            <div>
              <Label>{lang === "es" ? "Exámenes" : "Exams"}</Label>
              <div className="mt-2 max-h-72 overflow-auto brutal-sm p-3 bg-canales-cream space-y-2">
                {exams.map((e) => {
                  const req = form.required_exam_ids.includes(e.exam_id);
                  const bonus = form.bonus_exam_ids.includes(e.exam_id);
                  return (
                    <div key={e.exam_id} className="flex items-start justify-between gap-2 bg-white brutal-sm p-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-display font-bold uppercase tracking-widest text-canales-red">
                          {e.category}
                        </div>
                        <div className="font-body text-sm truncate">
                          {lang === "es" ? e.title_es : e.title_en}
                        </div>
                        <div className="text-xs text-neutral-500">
                          {e.question_ids?.length || 0} {lang === "es" ? "preguntas" : "questions"}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <label className="flex items-center gap-1 text-xs font-display font-bold">
                          <Checkbox checked={req} onCheckedChange={() => toggleReq(e.exam_id)} />
                          {lang === "es" ? "Oblig." : "Req."}
                        </label>
                        <label className="flex items-center gap-1 text-xs font-display font-bold">
                          <Checkbox checked={bonus} onCheckedChange={() => toggleBonus(e.exam_id)} />
                          Bonus
                        </label>
                      </div>
                    </div>
                  );
                })}
                {exams.length === 0 && (
                  <div className="text-sm font-body text-neutral-500">
                    {lang === "es" ? "No hay exámenes disponibles" : "No exams available"}
                  </div>
                )}
              </div>
              <div className="mt-2 text-xs font-body">
                <span className="font-display font-bold">
                  {form.required_exam_ids.length} {lang === "es" ? "obligatorios" : "required"}
                </span>
                {" · "}
                <span>{form.bonus_exam_ids.length} bonus</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              data-testid="admin-role-submit-btn"
              onClick={submit}
              className="brutal-btn bg-canales-blue text-white px-4 py-2"
            >
              {editing
                ? (lang === "es" ? "Guardar cambios" : "Save changes")
                : (lang === "es" ? "Crear rol" : "Create role")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
